import * as Sentry from "@sentry/react";
import {
  Button,
  Card,
  CardContent,
  Paragraph,
} from "@pocketsign/design-system";
import {
  CatchBoundary,
  useLocation,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { format } from "date-fns";
import { useState } from "react";
import { css } from "../../styled-system/css";
import { token } from "../../styled-system/tokens";
import {
  COMPANY_TOKEN_STORAGE_KEY,
  STORE_TOKEN_STORAGE_KEY,
} from "../libs/auth";

interface ErrorWithSentryEventId extends Error {
  sentryEventId?: string;
}

type Props = {
  children: React.ReactNode;
};

const errorPageStyle = css({
  minH: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bg: "background.background",
  px: "6",
});

const errorCardWrapperStyle = css({
  w: "full",
  maxW: "480px",
});

function ErrorPage({ error }: ErrorComponentProps) {
  const [time] = useState<number>(Date.now());
  const sentryEventId = (error as ErrorWithSentryEventId).sentryEventId;
  const location = useLocation();
  let backToLoginUrl = "/login/company";
  let tokenStorageKey: string | null = COMPANY_TOKEN_STORAGE_KEY;

  if (location.pathname.startsWith("/admin")) {
    backToLoginUrl = "/login/admin";
    tokenStorageKey = null;
  } else if (location.pathname.startsWith("/store")) {
    backToLoginUrl = "/login/store";
    tokenStorageKey = STORE_TOKEN_STORAGE_KEY;
  }

  return (
    <div
      data-panda-theme="v2"
      className={errorPageStyle}
      style={{
        color: token.var("colors.text.primary"),
      }}
    >
      <div className={errorCardWrapperStyle}>
        <Card>
          <CardContent>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                textAlign: "center",
              }}
            >
              <h1
                style={{
                  margin: 0,
                  color: token.var("colors.text.primary"),
                  fontSize: "20px",
                  fontWeight: 700,
                }}
              >
                エラーが発生しました
              </h1>
              <Paragraph color="secondary" size="sm">
                {format(time, "yyyy/MM/dd HH:mm:ss")}
              </Paragraph>
              {sentryEventId && (
                <Paragraph color="secondary" size="sm">
                  エラーID: {sentryEventId}
                </Paragraph>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <Button type="button" onClick={() => window.location.reload()}>
                  再読み込み
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    if (tokenStorageKey) {
                      localStorage.removeItem(tokenStorageKey);
                    }
                    window.location.href = backToLoginUrl;
                  }}
                >
                  ログイン画面に戻る
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export function ErrorBoundary({ children }: Props) {
  return (
    <CatchBoundary
      getResetKey={() => "reset"}
      onCatch={(error) => {
        console.error("Caught error:", error);

        const eventId = Sentry.captureException(error);
        (error as ErrorWithSentryEventId).sentryEventId = eventId;
      }}
      errorComponent={ErrorPage}
    >
      {children}
    </CatchBoundary>
  );
}
